import React, { type ReactNode, useCallback } from 'react';
import { utcToZonedTime } from 'date-fns-tz';
import Button from '@atlaskit/button';
import { FormattedI18nMessage } from '@atlassian/jira-formatted-i18n-message/src/ui/index.tsx';
import { type MessageDescriptor, useIntl, FormattedMessage } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import { messages } from './messages.tsx';

const issueFailuresTab = 'ISSUE_NOTIFICATION';

export const getLogDateTime = (
	dateString: string | number | Date | undefined,
	timeZone: string,
) => {
	if (dateString) {
		const dateObj = utcToZonedTime(dateString, timeZone);
		const year = dateObj.getFullYear();
		const month = dateObj.toLocaleString('en-us', { month: 'short' });
		const day = dateObj.getDate();
		const hours = dateObj.getHours();
		const minutes = dateObj.getMinutes();

		// Format the output date string
		const outputDateString = `${month} ${day}, ${year} ${hours
			.toString()
			.padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

		return outputDateString;
	}
	return dateString;
};

const FormattedErrorWithLinkButton = ({
	href,
	errorMessage,
	onLinkButtonClicked,
}: {
	href: string;
	errorMessage: MessageDescriptor;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onLinkButtonClicked: any;
}) => {
	const { formatMessage } = useIntl();
	return (
		<FormattedI18nMessage
			componentsMapping={{
				link: (props: { children: ReactNode }) => (
					<Button
						appearance="link"
						spacing="none"
						href={href}
						target="_blank"
						onClick={onLinkButtonClicked}
					>
						{props.children}
					</Button>
				),
			}}
			message={formatMessage(errorMessage, {
				linkStart: '{linkStart}',
				linkEnd: '{linkEnd}',
			})}
		/>
	);
};

const FormattedErrorText = ({ errorMessage }: { errorMessage: MessageDescriptor }) => {
	const { formatMessage } = useIntl();
	return <>{formatMessage(errorMessage)}</>;
};

export const BouncedError = ({
	mailboxMessage,
	formatMessage,
}: {
	mailboxMessage: string | null | undefined;
	formatMessage: FormatMessage;
}) => {
	const bouncedMessage = `${formatMessage(messages.bounced)}: ${mailboxMessage}`;
	return <>{bouncedMessage}</>;
};

export const ErrorMessage = ({
	errorKey,
	projectKey,
	mailboxMessage,
	formatMessage,
	tabSelected,
}: {
	errorKey: string | null | undefined;
	projectKey: string | null;
	mailboxMessage: string | null | undefined;
	formatMessage: FormatMessage;
	tabSelected?: string;
}): JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onInternalErrorResendInviteClicked = useCallback(
		() =>
			fireUIAnalytics(
				createAnalyticsEvent({
					actionSubject: 'internalErrorResendInviteLink',
					action: 'clicked',
				}),
				'internalErrorResendInvite',
			),
		[createAnalyticsEvent],
	);

	const suppressedErrorGoToCustomersPageClicked = useCallback(
		() =>
			fireUIAnalytics(
				createAnalyticsEvent({
					actionSubject: 'suppressedErrorGoToCustomersPageLink',
					action: 'clicked',
				}),
				'suppressedErrorGoToCustomersPage',
			),
		[createAnalyticsEvent],
	);

	switch (errorKey && errorKey.toLowerCase()) {
		case 'rendering_failed':
		case 'retrieve_configuration_failed':
		case 'retryable_am_failure':
		case 'unknown_failure': {
			const href = `/jira/servicedesk/projects/${projectKey}/customers`;
			return (
				<FormattedErrorWithLinkButton
					href={href}
					errorMessage={messages.renderingFailed}
					onLinkButtonClicked={onInternalErrorResendInviteClicked}
				/>
			);
		}
		case 'request_too_long':
		case 'request_size_exceeded':
			return <FormattedErrorText errorMessage={messages.requestTooLong} />;
		case 'email_validation_failure': {
			return <FormattedErrorText errorMessage={messages.emailValidationFailure} />;
		}
		case 'rfc1918_lan_ip_routing_failure':
			return <FormattedErrorText errorMessage={messages.rfc1918LanIpRoutingFailure} />;
		case 'suppressed': {
			if (tabSelected === issueFailuresTab) {
				return (
					<FormattedErrorText errorMessage={messages.suppressedMessageForRequestNotifications} />
				);
			}

			const href = `/jira/servicedesk/projects/${projectKey}/customers`;
			return (
				<FormattedErrorWithLinkButton
					href={href}
					errorMessage={messages.suppressedMessage}
					onLinkButtonClicked={suppressedErrorGoToCustomersPageClicked}
				/>
			);
		}
		case 'email_notification_limit_reached': {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const siteUrl = window.location.hostname;
			return <FormattedMessage values={{ siteUrl }} {...messages.emailNotificationLimitReached} />;
		}
		case 'bounced':
			return <BouncedError mailboxMessage={mailboxMessage} formatMessage={formatMessage} />;

		default:
			if (tabSelected === issueFailuresTab) {
				return (
					<FormattedErrorText errorMessage={messages.defaultErrorMessageForRequestNotification} />
				);
			}
			return <FormattedErrorText errorMessage={messages.defaultErrorMessage} />;
	}
};

// in future we would add more cases for different notification types.
export const getNotificationName = (notificationType: string, formatMessage: FormatMessage) => {
	switch (notificationType.toLowerCase()) {
		case 'customer_invite':
			return formatMessage(messages.customerInvited);
		case 'request_created':
			return formatMessage(messages.requestCreated);
		case 'public_comment_added':
			return formatMessage(messages.publicCommentAdded);
		case 'public_comment_edited':
			return formatMessage(messages.publicCommentEdited);
		case 'request_resolved':
			return formatMessage(messages.requestResolved);
		case 'request_reopened':
			return formatMessage(messages.requestReopened);
		case 'participant_added':
			return formatMessage(messages.participantAdded);
		case 'organisation_added':
			return formatMessage(messages.organizationAdded);
		case 'approval_required':
			return formatMessage(messages.approvalRequired);
		case 'customer_visible_status_changed':
			return formatMessage(messages.customerVisibleStatusChanged);
		case 'custom_notification':
			return formatMessage(messages.customNotification);
		default:
			return '';
	}
};
